import { OptimizelyDecideOption, type OptimizelyDecision } from '@optimizely/react-sdk';

import {
  type ABTestingFeatureFlag,
  type CustomFeatureFlag,
  type FeatureFlag,
  type RolloutFeatureFlag,
} from './featureFlags';
import { type VariablesForFlag } from './variableTypes';
import { DEFAULT_SPLIT_VARIATION, VariationTypes } from './variationTypes';

export type FlagOptions = {
  blockEvent?: boolean;
};

export type FeatureFlagResult<T extends FeatureFlag> = T extends RolloutFeatureFlag
  ? { enabled: boolean; variables: VariablesForFlag<T> }
  : T extends CustomFeatureFlag
  ? { variables: VariablesForFlag<T>; variation: T['variations'][number] }
  : T extends ABTestingFeatureFlag
  ? {
      variables: VariablesForFlag<T>;
      variation: (typeof VariationTypes.A_B_TESTING)[number];
    }
  : never;

const isCustomFeatureFlag = (featureFlag: FeatureFlag): featureFlag is CustomFeatureFlag =>
  featureFlag.variationType === VariationTypes.CUSTOM;

export const getDecideOptions = (options?: FlagOptions): OptimizelyDecideOption[] =>
  options?.blockEvent ? [OptimizelyDecideOption.DISABLE_DECISION_EVENT] : [];

export const transformDecision = <T extends FeatureFlag>(
  featureFlag: T,
  decision: OptimizelyDecision,
): FeatureFlagResult<T> => {
  if (featureFlag.variationType === VariationTypes.ROLLOUT) {
    return {
      enabled: decision.variationKey === 'on',
      variables: decision.variables,
    } as FeatureFlagResult<T>;
  }

  if (isCustomFeatureFlag(featureFlag)) {
    return {
      variables: decision.variables,
      variation: decision.variationKey as (typeof featureFlag.variations)[number],
    } as FeatureFlagResult<T>;
  }

  const variation =
    decision.variationKey === 'off' ? DEFAULT_SPLIT_VARIATION : decision.variationKey;

  return {
    variables: decision.variables,
    variation: variation as (typeof VariationTypes.A_B_TESTING)[number],
  } as FeatureFlagResult<T>;
};
